import React, { useEffect, useMemo, useState } from "react";
import { Trash2 } from "lucide-react";

import { Tooltip } from "../components/Tooltip";
import type { ConversationFeedItem } from "./client";
import { useConversationWorkspace } from "./ConversationWorkspace";
import { EMPTY_TAB_SLICE, feedStore, useFeedStoreState } from "./feedStore";
import {
  feedMiddleAnnounce,
  humanizeToken,
  lucideIconOfWord,
  rearrangeTabsAnnounce,
  lifecycleDeactivateAnnounce,
  templateForkAnnounce,
} from "./feedWave";
import { toneByIndex, toneVars } from "./tones";
import type { ChannelApi } from "./useChannel";

/* THE FEED WAVE (Ori's law: the client renders what the world declares): the
 * rail's tab strip IS the served feed row — `query {tab:"feed"}` feed.tab
 * TOKENS in the human's own (possibly re-arranged) order. Faces are purely
 * mechanical projections of the token (humanized label, mechanical lucide
 * lookup, initial fallback) — no hardcoded tab table; a new tab (e.g.
 * "template") arrives from world with ZERO code change. Cards render the
 * SERVED preview (conversation:preview primitives + artifact pointer through
 * tab:list:@(ui:kind:panel)**) — the client derives nothing. */

type JsonRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is JsonRecord => (
  typeof value === "object" && value !== null && !Array.isArray(value)
);

const humanToYou = (s: string): string => s === "human" ? "you" : s;

const itemTitleOf = (item: ConversationFeedItem): string => {
  // Title is the outlet-crossed template primitive. Never parse raw cached
  // row generations during React render: a stale cache must reconcile, not
  // unmount the entire client before its first query can run.
  if (isTemplateItem(item)) return item.title;
  if (isRecord(item.artifact)) {
    const machine = isRecord(item.artifact.machine) ? item.artifact.machine : {};
    const input = isRecord(item.artifact.input) ? item.artifact.input : {};
    const surface = isRecord(input.surface) ? input.surface : {};
    const titleIsGeneric = !item.title || ["integrations", "integration", "item"].includes(item.title.toLowerCase());
    const label = [
      machine.label,
      machine.name,
      surface.providerSurfaceLabel,
      surface.providerSurfaceName,
      surface.providerSurfaceId,
      surface.surfaceId,
    ].find((value) => typeof value === "string" && value.trim());
    if (titleIsGeneric && typeof label === "string" && label.trim()) return label.trim();
  }
  if (item.title) return humanToYou(item.title);
  return humanToYou(item.badge || "item");
};

/** The SERVED preview panel (sci//conversation:preview:@primitive** +
 * @(artifact:preview)**) — humanized primitives + the artifact preview
 * pointer, rendered AS-IS. No served preview → nothing (never a derived
 * fabrication). */
const artifactPointerLabel = (pointer: unknown): string => {
  if (typeof pointer === "string") return pointer;
  if (isRecord(pointer)) {
    const machine = isRecord(pointer.machine) ? pointer.machine : pointer;
    return String(machine.label || machine.name || machine.id || "");
  }
  return "";
};

const ServedPreview: React.FC<{ item: ConversationFeedItem }> = ({ item }) => {
  const preview = isRecord(item.preview) ? item.preview : null;
  const primitives = Array.isArray(preview?.primitive)
    ? preview!.primitive!.filter((line): line is string => typeof line === "string" && !!line.trim())
    : [];
  const pointerLabel = artifactPointerLabel(preview?.artifact);
  if (!primitives.length && !pointerLabel) return null;
  return (
    <span data-served-preview style={previewPanel} aria-label="Conversation preview">
      {pointerLabel && (
        <span data-preview-artifact style={previewArtifact} title={pointerLabel}>
          {pointerLabel}
        </span>
      )}
      {primitives.map((line, index) => (
        <span key={index} data-preview-line style={previewLine} title={line}>
          {line}
        </span>
      ))}
    </span>
  );
};

// ── the template family (Fork affordance) ────────────────────────────────────
/** The outlet owns the template card crossing and serves its one normalized
 * face. The client never reparses the raw row: an older cached generation is
 * inert until the first tab reconcile replaces it. */
const isTemplateItem = (item: ConversationFeedItem): boolean =>
  isRecord(item.template);

const TEMPLATE_KIND_TO_TAB = {
  "sci//template:kind:@entity": "book",
  "sci//template:kind:@render": "workspace",
  "sci//template:kind:@agent": "agent",
} as const;

type TemplateKindAtom = keyof typeof TEMPLATE_KIND_TO_TAB;
type TemplateTargetTab = (typeof TEMPLATE_KIND_TO_TAB)[TemplateKindAtom];
type TemplateContract = {
  primitive: string;
  ref: string;
  preview: string;
  previewMachine: string;
  kind: TemplateKindAtom;
  tab: TemplateTargetTab;
};

const templateForkCorrelationOf = (item: ConversationFeedItem): string => {
  return String(item.activationCorrelation || "");
};

// Announce acknowledges enqueue, not completion. A report-backed render can
// legitimately compose for tens of seconds, so every fork kind gets the same
// bounded three-minute reconciliation window instead of the old ~5.8s generic
// window that could strand a completed Finance fork without opening it.
const TEMPLATE_FORK_RECONCILE_ATTEMPTS = 180;
const TEMPLATE_FORK_RECONCILE_INTERVAL_MS = 1_000;
const STABLE_BACKBONE_REFORK_RECONCILE_ATTEMPTS = TEMPLATE_FORK_RECONCILE_ATTEMPTS;
const ARTIFACT_ACTIVATE_ATOM = "sci//artifact:activate:@activated";
const ENTITY_BACKBONE_ATOM = "sci//entity:kind:@backbone";
const ENTITY_BUCKET_ATOM = "sci//entity:kind:@bucket";

/** A backbone refork updates one existing Book artifact instead of minting a
 * second card. Identify that exceptional target only by its exact declaration:
 * template-fork lineage + source template ref + backbone (never bucket). The
 * target's presence is NOT completion — the new correlation must still land. */
const stableBackboneForkTargetOf = (
  items: ConversationFeedItem[],
  sourceRef: string,
): ConversationFeedItem | undefined => {
  const matches = items.filter((item) => {
    const raw = isRecord(item.raw) ? item.raw : {};
    const refs = new Set(
      (Array.isArray(raw.ref) ? raw.ref : [])
        .filter((ref): ref is string => typeof ref === "string"),
    );
    return refs.has(ARTIFACT_ACTIVATE_ATOM)
      && refs.has(sourceRef)
      && refs.has(ENTITY_BACKBONE_ATOM)
      && !refs.has(ENTITY_BUCKET_ATOM);
  });
  if (matches.length > 1) {
    throw new Error(
      `[template] source ${sourceRef} has ${matches.length} stable backbone fork targets`,
    );
  }
  return matches[0];
};

/** The template card has its own crossing: primitive = human label, ref =
 * source artifact for Fork, preview = artifact for Open, and exactly one kind
 * atom selects the destination tab. The row machine is lifecycle identity
 * only and never participates in these decisions. */
const templateContractOf = (item: ConversationFeedItem): TemplateContract => {
  if (!isTemplateItem(item)) {
    throw new Error(`[template] ${item.id || "<unknown>"} does not declare the template family`);
  }
  const face = item.template;
  if (!face || !Object.prototype.hasOwnProperty.call(TEMPLATE_KIND_TO_TAB, face.kind)) {
    throw new Error(
      `[template] ${item.id || "<unknown>"} has no current served template face`,
    );
  }
  return {
    primitive: face.primitive,
    ref: face.ref,
    preview: face.preview,
    previewMachine: face.previewMachine,
    kind: face.kind,
    tab: TEMPLATE_KIND_TO_TAB[face.kind],
  };
};

/** A template row is not the artifact it previews. Open follows the explicit
 * template:preview lane and never the template lifecycle machine or source
 * ref lane. */
const templateItemForOpen = (item: ConversationFeedItem): ConversationFeedItem => {
  if (!isTemplateItem(item)) return item;
  const contract = templateContractOf(item);
  const artifact = isRecord(item.artifact) ? item.artifact : {};
  const machine = isRecord(artifact.machine) ? artifact.machine : {};
  return {
    ...item,
    artifact: {
      ...artifact,
      machine: {
        ...machine,
        id: contract.previewMachine,
        name: contract.previewMachine,
      },
    },
  };
};

/** The served tab face icon: the tab row's own BARE lucide word mapped
 * mechanically onto the lucide-react icon set (an unknown word errors LOUD
 * in the lookup); the VISIBLE fallback glyph is the label's initial — never
 * silent nothing. */
const TabIcon: React.FC<{ word: string; label: string }> = ({ word, label }) => {
  const Icon = lucideIconOfWord(word);
  if (Icon) return <Icon size={12} />;
  return <span aria-hidden="true" style={{ fontSize: 10, fontWeight: 700 }}>{(label || "?").charAt(0).toUpperCase()}</span>;
};

export const ConversationSection: React.FC<{
  ch: ChannelApi;
  isPlatformAdmin: boolean;
}> = ({ ch, isPlatformAdmin }) => {
  const workspace = useConversationWorkspace();
  const channelQuery = ch.query;
  const channelAnnounce = ch.announce;
  const userId = ch.userId;
  const [deactivating, setDeactivating] = useState<Set<string>>(() => new Set());

  // THE APP STORE (§4): the rail renders the ONE channel store — the served
  // feed row, the per-tab item lists, the inbox all live there. The rail
  // renders the store AS-IS (snapshot-instant); ensure* RECONCILES once per
  // page lifetime — a close/reopen refires NOTHING.
  const feed = useFeedStoreState();
  const channelReady = ch.ready;
  useEffect(() => {
    // never before auth — a token-less ask is a guaranteed 401 (the zero-401
    // browser law); ensureArrangement is single-flight.
    if (!channelReady) return;
    void feedStore.ensureArrangement(channelQuery);
  }, [channelQuery, channelReady]);

  // The effective tab is the oracle-projected middle selection when it still
  // exists in the served order; otherwise the FIRST served token. Current
  // selection is world state, never conversation identity or a loading flag.
  const servedTokens = useMemo(() => feed.feed?.tab ?? [], [feed.feed?.tab]);
  const declaredMiddle = feed.feed?.middle || "";
  const activeTab = useMemo(() => {
    if (declaredMiddle && servedTokens.includes(declaredMiddle)) return declaredMiddle;
    return servedTokens[0] || "";
  }, [declaredMiddle, servedTokens]);

  const slice = feed.byTab[activeTab] || EMPTY_TAB_SLICE;

  // Tab content — the outlet resolves each tab's OWN declared selector
  // (`query {tab:"<token>"}`); the store DRAINS serial 1..total per the
  // served sync.totals (§1) and keeps the list for the whole session — a tab
  // revisit renders the held slice with zero asks.
  useEffect(() => {
    // The arrangement crossing reprojects persisted tab selectors before any
    // tab ask may consume them. Snapshot items can remain visible while that
    // declared status is pending, but they never start a read with a retired
    // selector.
    if (!channelReady || !feed.arrangementLoaded || !activeTab) return;
    void feedStore.ensureTab(activeTab, channelQuery);
  }, [channelQuery, channelReady, feed.arrangementLoaded, activeTab]);

  const selectTab = (token: string) => {
    if (!userId || token === activeTab) return;
    feedStore.applyFeedMiddle(token);
    void channelAnnounce(feedMiddleAnnounce(userId, token))
      .catch((error) => console.error("[feed] the middle-tab write failed (never reached the world):", error));
  };

  // ── RE-ARRANGE (minimal affordance): right-click a served tab moves it to
  // the FRONT and writes the full new order (`sci//feed:left:@tab**`) — the
  // world claims the human-confidence arrangement map (keyed, idempotent);
  // the store folds the SAME order locally — no refetch.
  const rearrangeToFront = (token: string) => {
    if (!userId || !servedTokens.includes(token)) return;
    const order = [token, ...servedTokens.filter((id) => id !== token)];
    if (JSON.stringify(order) === JSON.stringify(servedTokens)) return;
    void channelAnnounce(rearrangeTabsAnnounce(userId, order))
      .then(() => feedStore.applyTabOrder(order))
      .catch((error) => console.error("[feed] the re-arrange write failed (never reached the world):", error));
  };

  // ── THE ACTIVATION (sci//artifact:activate:@activated) — source, human
  // label, preview, exact kind and destination tab ride the first-class
  // template crossing. The result belongs to that kind's tab, never Templates.
  const forkTemplate = (event: React.MouseEvent, item: ConversationFeedItem) => {
    event.stopPropagation();
    if (!userId) return;
    let contract: TemplateContract;
    try {
      contract = templateContractOf(item);
    } catch (error) {
      console.error(error);
      return;
    }
    if (!servedTokens.includes(contract.tab)) {
      console.error(
        `[template] fork refused — kind ${contract.kind} targets missing served tab ${contract.tab}`,
        item,
      );
      return;
    }
    const fork = templateForkAnnounce(userId, {
      ref: contract.ref,
      primitive: contract.primitive,
      preview: contract.preview,
      kind: contract.kind,
      tab: contract.tab,
    });
    // THE OWNERSHIP IS FULL (Ori 2026-08-04): the CLIENT causes the fork, so
    // the client owns everything the human must SEE from the click — the
    // conversation opens, the coming artifact's placeholder stands where the
    // box will mount, and progress PRINTS into it as the crossing advances
    // (read triggers write, the write takes time — nothing happens magically
    // off-screen). The placeholder is client-local (never announced) and the
    // real box supersedes it the moment the conversation re-mints it in.
    const requestStamp = new Date().toISOString().replace("Z", "");
    workspace.setActiveConversationId(fork.conversationId, contract.primitive);
    workspace.setWorkspaceError("");
    const printProgress = (text: string, status: "start" | "complete" = "start") =>
      ch.hydrateAnnouncements([{
        id: `local-fork-${fork.conversationId}`,
        timestamp: requestStamp,
        conversationId: fork.conversationId,
        sender: "human",
        kind: "channel",
        error: [],
        recipient: [],
        refs: [],
        reply: [],
        render: [{
          mode: "file",
          suffix: "md",
          payload: { text },
          machine: { name: "message", id: `local-fork-${fork.conversationId}`, status },
        }],
      }]);
    printProgress(`Fork **${contract.primitive}** — requested. The world is composing the artifact; it mounts here on its first serve.`);
    feedStore.applyFeedMiddle(contract.tab);
    void channelAnnounce(feedMiddleAnnounce(userId, contract.tab))
      .catch((error) => console.error("[template] the destination-tab write failed (never reached the world):", error));
    void channelAnnounce(fork)
      .then(async () => {
        // Announce acknowledges enqueue, not world completion. Reconcile the
        // destination until this exact fork arrives; one immediate refresh can
        // race the crossing and leave a loaded-but-stale tab for the session.
        let attemptLimit = TEMPLATE_FORK_RECONCILE_ATTEMPTS;
        let stableBackboneTargetId = "";
        let landed: ConversationFeedItem | undefined;
        const historyCarriesArtifact = (history: unknown[]): boolean =>
          history.some((row) => {
            const r = row as {
              artifact?: unknown;
              body?: { artifact?: unknown };
              payload?: { artifact?: unknown };
            };
            const shells = [r?.artifact, r?.body?.artifact, r?.payload?.artifact]
              .find((lane) => Array.isArray(lane) && lane.length > 0);
            return Array.isArray(shells) && shells.length > 0;
          });
        for (let attempt = 0; attempt < attemptLimit; attempt += 1) {
          await new Promise((resolve) => window.setTimeout(
            resolve,
            TEMPLATE_FORK_RECONCILE_INTERVAL_MS,
          ));
          if (!landed) {
            await feedStore.refreshTab(contract.tab, channelQuery);
            const candidates = feedStore.getState().byTab[contract.tab]?.items ?? [];
            landed = contract.tab === "workspace"
              ? candidates.find((candidate) => (
                candidate.id === fork.conversationId
                || candidate.conversationId === fork.conversationId
              ))
              : candidates.find((candidate) => (
                templateForkCorrelationOf(candidate) === fork.conversationId
              ));
            if (landed) {
              printProgress(`Fork **${contract.primitive}** — the world claimed the artifact; pulling it into this conversation…`);
              workspace.openConversation(landed);
              if (contract.tab !== "workspace") return;
            } else if (
              contract.kind === "sci//template:kind:@entity"
              && contract.tab === "book"
            ) {
              const stableTarget = stableBackboneForkTargetOf(candidates, contract.ref);
              if (stableTarget) {
                if (
                  stableBackboneTargetId
                  && stableBackboneTargetId !== stableTarget.id
                ) {
                  throw new Error(
                    `[template] stable backbone target changed from ${stableBackboneTargetId} to ${stableTarget.id}`,
                  );
                }
                stableBackboneTargetId = stableTarget.id;
                // Hydrating Financial can take materially longer than minting a
                // correlation-keyed generic fork. Keep polling this exact stable
                // target, but never accept its pre-write row as completion.
                attemptLimit = STABLE_BACKBONE_REFORK_RECONCILE_ATTEMPTS;
              }
              continue;
            } else {
              continue;
            }
          }
          // THE BOX MUST BE SEEN (ownership is full): the world holds the
          // artifact announcement, so RE-MINT the conversation until it is
          // local — never wait on a ws frame the transport may have dropped
          // (the fork box arriving minutes late was exactly that wait).
          const history = await ch.ensureConversation(
            fork.conversationId, undefined, { refresh: true },
          );
          if (historyCarriesArtifact(history)) {
            printProgress(
              `Forked **${contract.primitive}** — the artifact is live below.`,
              "complete",
            );
            return;
          }
        }
        throw new Error(
          stableBackboneTargetId
            ? `[template] accepted fork ${fork.conversationId} did not refresh stable backbone ${stableBackboneTargetId}`
            : landed
              ? `[template] accepted fork ${fork.conversationId} landed on ${contract.tab} but its artifact announcement never re-minted into the conversation`
              : `[template] accepted fork ${fork.conversationId} did not appear in ${contract.tab}`,
        );
      })
      .catch((error) => console.error("[template] the fork write failed (never reached the world):", error));
  };

  const openItem = (item: ConversationFeedItem) => {
    // ONE open path: a conversation item opens its conversation; an artifact
    // item (a Book card — the queue instances included) opens the artifact's
    // logical conversation (openConversation falls back to the artifact
    // machine id).
    try {
      workspace.openConversation(templateItemForOpen(item));
    } catch (error) {
      console.error(error);
    }
  };

  const canDeactivate = (item: ConversationFeedItem): boolean => {
    const target = item.deactivation;
    if (!target) return false;
    if (target.kind === "template") return isPlatformAdmin;
    return !!userId && target.scopePath === `sci::human:${userId}`;
  };

  const deactivateItem = (
    event: React.MouseEvent | React.KeyboardEvent,
    item: ConversationFeedItem,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    const target = item.deactivation;
    if (!userId || !target || !canDeactivate(item) || deactivating.has(target.address)) return;
    const originalIndex = Math.max(
      0,
      (feedStore.getState().byTab[item.tab]?.items ?? []).findIndex(
        (candidate) => candidate.id === item.id,
      ),
    );
    setDeactivating((current) => new Set(current).add(target.address));
    feedStore.removeTabItem(item.tab, item.id);
    void channelAnnounce(lifecycleDeactivateAnnounce(userId, target))
      .then(async () => {
        // Announce acknowledges enqueue, not the world claim. Reconcile until
        // this exact lifecycle address is absent from the binary-active read.
        for (let attempt = 0; attempt < 120; attempt += 1) {
          await new Promise((resolve) => window.setTimeout(resolve, 500));
          await feedStore.refreshTab(item.tab, channelQuery);
          const refreshed = feedStore.getState().byTab[item.tab] || EMPTY_TAB_SLICE;
          if (refreshed.error) {
            throw new Error(`[lifecycle] ${item.tab} reconcile failed: ${refreshed.error}`);
          }
          const candidates = refreshed.items;
          if (!candidates.some((candidate) => (
            candidate.deactivation?.address === target.address
          ))) {
            if ([item.id, item.conversationId, target.machine].some(
              (identity) => !!identity && identity === workspace.conversationId,
            )) {
              workspace.clearActiveConversation();
            }
            return;
          }
        }
        throw new Error(
          `[lifecycle] accepted ${target.kind} deactivation did not remove ${target.address}`,
        );
      })
      .catch(async (error) => {
        console.error("[lifecycle] deactivation failed:", error);
        // Restore from world on a refused/failed write; no optimistic ghost.
        feedStore.restoreTabItem(item.tab, item, originalIndex);
        await feedStore.refreshTab(item.tab, channelQuery);
      })
      .finally(() => {
        setDeactivating((current) => {
          const next = new Set(current);
          next.delete(target.address);
          return next;
        });
      });
  };

  const railItems = slice.items;
  // §29: the ERROR state renders LOUD and AHEAD of loading; stale items from
  // the snapshot keep rendering (stale-from-snapshot is fine, blocked-on-
  // network is not) — loading only shows while there is nothing to render.
  const showError = !!(feed.arrangementError || slice.error);
  const showLoading = !showError
    && railItems.length === 0
    && (feed.arrangementLoading || slice.loading || (!feed.arrangementLoaded && servedTokens.length === 0));

  return (
    <aside className="conversation-section workspace-section" data-edge="left" aria-label="Conversation section">
      <div className="conversation-tabs" role="tablist" aria-label="Conversation section tabs" style={{ position: "relative" }}>
        {/* The strip renders the served token order VERBATIM (no client
            sorting); each face is the SERVED tab row (humanized label +
            description + bare icon word) — the tooltip shows title + the
            REAL description. A token whose face has not been served yet
            (ws-only boot) renders the mechanical humanized token, no icon. */}
        {servedTokens.map((token) => {
          const active = activeTab === token;
          const face = feed.feed?.tabs.find((t) => t.id === token);
          const label = face?.label || humanizeToken(token);
          const description = face?.description || "";
          return (
            <Tooltip
              key={token}
              label={label}
              {...(description ? { description } : {})}
              className="conversation-tab-tooltip-host"
            >
              <button
                type="button"
                role="tab"
                aria-selected={active}
                aria-label={label}
                onClick={() => selectTab(token)}
                onContextMenu={(event) => { event.preventDefault(); rearrangeToFront(token); }}
                className="conversation-tab"
                data-tab-id={token}
                data-active={active ? "true" : "false"}
              >
                <TabIcon word={face?.icon ?? ""} label={label} />
              </button>
            </Tooltip>
          );
        })}
      </div>

      <div className="conversation-feed-grid workspace-section-scroll" aria-busy={showLoading}>
        {showLoading && <div className="conversation-empty workspace-section-state" data-kind="loading">Loading...</div>}
        {showError && railItems.length === 0 && (
          <div className="conversation-empty workspace-section-state" data-kind="error" title={feed.arrangementError || slice.error}>
            {feed.arrangementError || slice.error}
          </div>
        )}
        {!showLoading && !showError && railItems.length === 0 && <div className="conversation-empty workspace-section-state" data-kind="empty">No items yet.</div>}
        {!showLoading && railItems.map((item, index) => {
          const template = isTemplateItem(item);
          const deactivate = canDeactivate(item);
          const deactivationBusy = !!item.deactivation && deactivating.has(item.deactivation.address);
          return (
            <button
              key={`${item.tab}:${item.id}`}
              type="button"
              className="conversation-feed-card"
              data-primary-kind={String(item.badge || `item-${index}`)}
              data-feed-item-id={item.id}
              data-template={template ? "true" : undefined}
              style={toneVars(toneByIndex(index), "conversation-card")}
              onClick={() => openItem(item)}
              title={item.subtitle || item.title}
            >
              <span className="conversation-card-title">
                {itemTitleOf(item)}
              </span>
              <ServedPreview item={item} />
              {(template || deactivate) && (
                <span style={cardActions}>
                  {template && (
                    <span
                      role="button"
                      tabIndex={0}
                      data-template-activate
                      style={forkAction}
                      onClick={(event) => forkTemplate(event, item)}
                      onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") forkTemplate(event as unknown as React.MouseEvent, item); }}
                      title="Activate this template"
                    >
                      Activate
                    </span>
                  )}
                  {deactivate && item.deactivation && (
                    <span
                      role="button"
                      tabIndex={deactivationBusy ? -1 : 0}
                      aria-disabled={deactivationBusy}
                      aria-label={`Deactivate ${itemTitleOf(item)}`}
                      data-lifecycle-deactivate={item.deactivation.kind}
                      data-lifecycle-address={item.deactivation.address}
                      style={{ ...deactivateAction, opacity: deactivationBusy ? 0.45 : 1 }}
                      onClick={(event) => deactivateItem(event, item)}
                      onKeyDown={(event) => {
                        if (!deactivationBusy && (event.key === "Enter" || event.key === " ")) {
                          deactivateItem(event, item);
                        }
                      }}
                      title={`Deactivate ${itemTitleOf(item)}`}
                    >
                      <Trash2 size={13} aria-hidden="true" />
                    </span>
                  )}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </aside>
  );
};

const previewPanel: React.CSSProperties = {
  display: "grid",
  gap: 2,
  minWidth: 0,
  marginTop: 4,
};
const previewArtifact: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 650,
  color: "var(--color-text-secondary)",
  border: "1px solid var(--color-border)",
  borderRadius: 6,
  padding: "1px 6px",
  justifySelf: "start",
  maxWidth: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
const previewLine: React.CSSProperties = {
  fontSize: 10.5,
  color: "var(--color-text-secondary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
const forkAction: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  height: 22,
  padding: "0 10px",
  borderRadius: 7,
  border: "1px solid var(--color-border)",
  background: "transparent",
  color: "var(--color-text-secondary)",
  fontSize: 10.5,
  fontWeight: 650,
  cursor: "pointer",
};
const cardActions: React.CSSProperties = {
  marginTop: 6,
  justifySelf: "start",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
};
const deactivateAction: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 24,
  height: 22,
  borderRadius: 7,
  border: "1px solid var(--color-border)",
  background: "transparent",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
};
